import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import { useSparePartsNotifications } from "./useSparePartsNotifications";
import { SparePartsNotification } from "./types";

const routeForNotification = (notification: SparePartsNotification) => {
  const referenceId = encodeURIComponent(notification.referenceId);

  switch (notification.referenceType) {
    case "MAINTENANCE_TICKET":
    case "SERVICE_COMPLETION":
      return `/spare-parts/maintenance?ticketId=${referenceId}`;
    case "STOCK_REQUEST":
      return `/spare-parts/operations?tab=requests&recordId=${referenceId}`;
    case "SPARE_REQUEST":
      return `/spare-parts/operations?tab=issues&recordId=${referenceId}`;
    case "STOCK_TRANSFER":
      return `/spare-parts/operations?tab=transfers&recordId=${referenceId}`;
    case "PURCHASE_BILL":
      return `/spare-parts/procurement?recordId=${referenceId}`;
    case "SPARE_ISSUE":
      return `/spare-parts/operations?tab=issues&recordId=${referenceId}`;
    case "SPARE_RETURN":
      return `/spare-parts/operations?tab=returns&recordId=${referenceId}`;
    default:
      return "/spare-parts";
  }
};

const formatTime = (value: string) =>
  new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));

const isOnRoadSlaAlert = (notification: SparePartsNotification) =>
  notification.type.startsWith("ON_ROAD_TICKET_SLA_");

interface SparePartsNotificationBellProps {
  color?: "inherit" | "default" | "primary";
}

const SparePartsNotificationBell = ({
  color = "inherit",
}: SparePartsNotificationBellProps) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const {
    notifications,
    unreadCount,
    loading,
    latestNotification,
    clearLatestNotification,
    enableNotificationSound,
    markRead,
    markAllRead,
  } = useSparePartsNotifications();

  const sortedNotifications = useMemo(
    () =>
      [...notifications].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [notifications]
  );

  const open = Boolean(anchorEl);

  const handleNotificationClick = async (notification: SparePartsNotification) => {
    if (!notification.isRead) {
      await markRead(notification.id);
    }

    setAnchorEl(null);
    navigate(routeForNotification(notification));
  };

  const handleLatestNotificationClick = async () => {
    if (!latestNotification) return;

    await handleNotificationClick(latestNotification);
    clearLatestNotification();
  };

  return (
    <>
      <IconButton
        color={color}
        onClick={(event) => {
          enableNotificationSound();
          setAnchorEl(event.currentTarget);
        }}
        aria-label="Spare Parts notifications"
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 380,
            maxWidth: "calc(100vw - 24px)",
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2, py: 1.5 }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={800}>
              Notifications
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Spare Parts updates
            </Typography>
          </Box>

          <Button
            size="small"
            disabled={!unreadCount}
            onClick={markAllRead}
          >
            Mark all read
          </Button>
        </Stack>

        <Divider />

        <List disablePadding sx={{ maxHeight: 420, overflow: "auto" }}>
          {sortedNotifications.length === 0 && (
            <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {loading ? "Loading notifications..." : "No notifications yet."}
              </Typography>
            </Box>
          )}

          {sortedNotifications.map((notification) => (
            (() => {
              const slaAlert = isOnRoadSlaAlert(notification);

              return (
                <ListItemButton
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    alignItems: "flex-start",
                    bgcolor: slaAlert
                      ? notification.isRead
                        ? "rgba(211, 47, 47, 0.04)"
                        : "rgba(211, 47, 47, 0.12)"
                      : notification.isRead
                        ? "transparent"
                        : "rgba(37, 99, 235, 0.08)",
                    borderLeft: `3px solid ${
                      slaAlert ? "#d32f2f" : notification.isRead ? "transparent" : "#2563EB"
                    }`,
                    py: 1.25,
                  }}
                >
                  {slaAlert && <ErrorOutlineIcon color="error" sx={{ mr: 1, mt: 0.15 }} />}
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2" fontWeight={800} color={slaAlert ? "error.main" : undefined}>
                          {notification.title}
                        </Typography>
                        {slaAlert && (
                          <Typography variant="caption" color="error.main" fontWeight={800}>
                            ATTENTION
                          </Typography>
                        )}
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.75} mt={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(notification.createdAt)}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItemButton>
              );
            })()
          ))}
        </List>
      </Menu>

      <Snackbar
        open={Boolean(latestNotification)}
        autoHideDuration={8000}
        onClose={clearLatestNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={
            latestNotification && isOnRoadSlaAlert(latestNotification)
              ? "warning"
              : "info"
          }
          variant="filled"
          onClose={clearLatestNotification}
          action={
            <Button color="inherit" size="small" onClick={handleLatestNotificationClick}>
              View
            </Button>
          }
          sx={{ alignItems: "flex-start", maxWidth: 440 }}
        >
          <Typography variant="subtitle2" fontWeight={800}>
            {latestNotification?.title}
          </Typography>
          <Typography variant="body2">{latestNotification?.message}</Typography>
        </Alert>
      </Snackbar>
    </>
  );
};

export default SparePartsNotificationBell;
